import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider, Skeleton } from "@mui/material";

import { ServiceRequestDetails } from "../../../types/service.type";
import {
  CompletedJobsIcon,
  PreferredAgeIcon,
  ServiceDeliveryIcon,
  ServiceLocationIcon,
} from "../../../assets/icons/go-hire-icons";
import {
  SummaryHeader,
  SummaryHeaderLoader,
  SummaryInfo,
  SummaryInfoWithLoader,
  SummaryInfoWithChips,
  SummaryProviderDetails,
  SummaryProviderDetailsLoader,
  SummaryShare,
  SummaryShareLoader,
} from "../../../components/Summary";

import ServiceRequestActions from "./ServiceRequestActions";

const ServiceRequestSummary = ({
  serviceRequest,
  isLoading,
  isContractPaid,
}: {
  serviceRequest?: ServiceRequestDetails;
  isLoading: boolean;
  isContractPaid: boolean;
}) => {
  const { t } = useTranslation("goService", { keyPrefix: "serviceRequestPage" });
  if (isLoading) return <ServiceRequestSummaryLoader />;
  if (!serviceRequest) return null;

  const { completedJobs = 0, customerPreferenceConfig, availability } = serviceRequest.service;
  const serviceLocations = availability?.locations ?? [];
  const isOnline = Boolean(serviceLocations.length && serviceLocations.length > 0);
  const ageRange = customerPreferenceConfig?.ageRange;

  const summaryDetails = [
    {
      icon: <CompletedJobsIcon />,
      title: t("completedJobs"),
      value: completedJobs,
    },
    {
      icon: <ServiceDeliveryIcon />,
      title: t("serviceDelivery"),
      value: isOnline ? t("online") : t("offline"),
    },

    {
      icon: <PreferredAgeIcon />,
      title: t("preferredAge"),
      value:
        ageRange && ageRange.minAge && ageRange.maxAge
          ? t("fromTo", {
              from: ageRange.minAge,
              to: ageRange.maxAge,
            })
          : "--/--",
    },
    {
      icon: <ServiceLocationIcon />,
      title: t("serviceLocation"),
      value: serviceLocations,
    },
  ];

  const user = serviceRequest?.serviceProvider || serviceRequest?.requester;

  return (
    <Box sx={styles.container}>
      <SummaryHeader
        amount={serviceRequest.service.paymentConfig.amount}
        currency={serviceRequest.service.paymentConfig.currency}
        type={serviceRequest.service.type}
        expiryDate={serviceRequest.service.paymentConfig.expiryDate}
        unit={serviceRequest.service.paymentConfig.unit}
      />
      <Divider />
      <Box sx={styles.summaryDetails}>
        {summaryDetails.map(({ icon, title, value }, idx) => (
          <Fragment key={idx}>
            {Array.isArray(value) && (
              <SummaryInfoWithChips icon={icon} title={title} firstItemName={value[0]} totalLength={value.length} />
            )}
            {!Array.isArray(value) && <SummaryInfo icon={icon} title={title} value={value} />}
          </Fragment>
        ))}
      </Box>
      <Divider />
      <SummaryProviderDetails user={user!} isProvider={Boolean(serviceRequest?.serviceProvider)} />
      <ServiceRequestActions serviceRequest={serviceRequest} isContractPaid={isContractPaid} />
      <Divider />
      <SummaryShare title={t("shareService")} />
    </Box>
  );
};

export default ServiceRequestSummary;

const ServiceRequestSummaryLoader = () => {
  const { t } = useTranslation("goService", { keyPrefix: "serviceRequestPage" });

  return (
    <Box sx={styles.container}>
      <SummaryHeaderLoader />
      <Divider />
      <SummaryInfoWithLoader icon={<ServiceDeliveryIcon />} title={t("serviceDelivery")} />
      <SummaryInfoWithLoader icon={<PreferredAgeIcon />} title={t("preferredAge")} />
      <SummaryInfoWithLoader icon={<ServiceLocationIcon />} title={t("serviceLocation")} />
      <Divider />
      <SummaryProviderDetailsLoader />
      <Divider />
      <Skeleton variant="rounded" width="100%" height={44} />
      <Divider />
      <SummaryShareLoader />
    </Box>
  );
};

const styles = {
  container: {
    position: "sticky",
    top: "10px",
    display: "flex",
    width: { xs: "100%", md: "300px" },
    height: "fit-content",
    padding: "24px 16px",
    flexDirection: "column",
    gap: "24px",
    borderRadius: "8px",
    border: "1px solid #E7E7E7",
    background: "var(--Gray-White, #FFF)",
  },
  summaryDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
};
